package io.jsonize.web.compare

import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonNull
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.doubleOrNull
import kotlin.math.abs
import kotlin.math.round

@Serializable
enum class DiffKind {
    Unchanged,
    Added,
    Removed,
    Changed,
    TypeChanged
}

data class DiffOptions(
    val ignoreArrayOrder: Boolean = false,
    val includePaths: List<String> = emptyList(),
    val ignoreKeys: Set<String> = emptySet(),
    val numberTolerance: Double = 0.0
)

data class DiffNode(
    val name: String?,
    val path: String,
    val displayPath: String,
    val left: JsonElement?,
    val right: JsonElement?,
    val kind: DiffKind,
    val children: List<DiffNode> = emptyList()
)

@Serializable
data class DiffEntry(
    val kind: DiffKind,
    val path: String,
    val displayPath: String,
    val left: JsonElement? = null,
    val right: JsonElement? = null
)

data class DiffSummary(
    val added: Int,
    val removed: Int,
    val changed: Int,
    val typeChanged: Int
) {
    val total: Int = added + removed + changed + typeChanged

    companion object {
        fun from(entries: List<DiffEntry>): DiffSummary {
            var added = 0
            var removed = 0
            var changed = 0
            var typeChanged = 0
            for (entry in entries) {
                when (entry.kind) {
                    DiffKind.Added -> added++
                    DiffKind.Removed -> removed++
                    DiffKind.Changed -> changed++
                    DiffKind.TypeChanged -> typeChanged++
                    DiffKind.Unchanged -> Unit
                }
            }
            return DiffSummary(
                added = added,
                removed = removed,
                changed = changed,
                typeChanged = typeChanged
            )
        }
    }
}

@Serializable
data class JsonPatchOp(
    val op: String,
    val path: String,
    val value: JsonElement? = null
)

data class JsonDiffResult(
    val root: DiffNode,
    val entries: List<DiffEntry>,
    val summary: DiffSummary,
    val patch: List<JsonPatchOp>
)

fun diffJson(
    left: JsonElement?,
    right: JsonElement?,
    options: DiffOptions
): JsonDiffResult {
    val root = diffNode(null, emptyList(), left, right, options)
    val filteredRoot = filterTree(root, options)
    val entries = mutableListOf<DiffEntry>()
    collectEntries(filteredRoot, entries)
    val summary = DiffSummary.from(entries)
    val patch = if (options.ignoreArrayOrder) {
        emptyList()
    } else {
        buildPatch(entries)
    }
    return JsonDiffResult(
        root = filteredRoot,
        entries = entries,
        summary = summary,
        patch = patch
    )
}

private sealed class PathSegment {
    data class Key(val value: String) : PathSegment()
    data class Index(val value: Int) : PathSegment()
}

private fun diffNode(
    name: String?,
    segments: List<PathSegment>,
    left: JsonElement?,
    right: JsonElement?,
    options: DiffOptions
): DiffNode {
    val path = pointerPath(segments)
    val displayPath = displayPath(segments)

    if (left == null && right == null) {
        return DiffNode(name, path, displayPath, null, null, DiffKind.Unchanged)
    }

    if (left == null) {
        val children = buildMissingChildren(segments, null, right, options)
        return DiffNode(name, path, displayPath, null, right, DiffKind.Added, children)
    }

    if (right == null) {
        val children = buildMissingChildren(segments, left, null, options)
        return DiffNode(name, path, displayPath, left, null, DiffKind.Removed, children)
    }

    return when {
        left is JsonObject && right is JsonObject -> {
            val keys = linkedSetOf<String>()
                .apply {
                    addAll(left.keys)
                    addAll(right.keys)
                }
                .toList()
            val children = keys.mapNotNull { key ->
                if (options.ignoreKeys.contains(key.lowercase())) {
                    null
                } else {
                    val childSegments = segments + PathSegment.Key(key)
                    val childName = key
                    diffNode(
                        name = childName,
                        segments = childSegments,
                        left = left[key],
                        right = right[key],
                        options = options
                    )
                }
            }
            val kind = if (children.any { it.kind != DiffKind.Unchanged }) {
                DiffKind.Changed
            } else {
                DiffKind.Unchanged
            }
            DiffNode(name, path, displayPath, left, right, kind, children)
        }
        left is JsonArray && right is JsonArray -> {
            val leftList = left.toList()
            val rightList = right.toList()
            val leftSorted = if (options.ignoreArrayOrder) {
                leftList.sortedBy { canonicalize(it, options) }
            } else {
                leftList
            }
            val rightSorted = if (options.ignoreArrayOrder) {
                rightList.sortedBy { canonicalize(it, options) }
            } else {
                rightList
            }
            val max = maxOf(leftSorted.size, rightSorted.size)
            val children = (0 until max).map { idx ->
                val childSegments = segments + PathSegment.Index(idx)
                val childName = "[$idx]"
                diffNode(
                    name = childName,
                    segments = childSegments,
                    left = leftSorted.getOrNull(idx),
                    right = rightSorted.getOrNull(idx),
                    options = options
                )
            }
            val kind = if (children.any { it.kind != DiffKind.Unchanged }) {
                DiffKind.Changed
            } else {
                DiffKind.Unchanged
            }
            DiffNode(name, path, displayPath, left, right, kind, children)
        }
        left is JsonPrimitive && right is JsonPrimitive -> {
            val kind = comparePrimitives(left, right, options)
            DiffNode(name, path, displayPath, left, right, kind)
        }
        else -> {
            DiffNode(name, path, displayPath, left, right, DiffKind.TypeChanged)
        }
    }
}

private fun buildMissingChildren(
    segments: List<PathSegment>,
    left: JsonElement?,
    right: JsonElement?,
    options: DiffOptions
): List<DiffNode> {
    return when (val element = right ?: left) {
        is JsonObject -> element.entries.mapNotNull { (key, value) ->
            if (options.ignoreKeys.contains(key.lowercase())) {
                null
            } else {
                val childSegments = segments + PathSegment.Key(key)
                val childName = key
                diffNode(
                    name = childName,
                    segments = childSegments,
                    left = if (right == null) value else null,
                    right = if (right == null) null else value,
                    options = options
                )
            }
        }
        is JsonArray -> {
            val list = element.toList()
            list.mapIndexed { idx, value ->
                val childSegments = segments + PathSegment.Index(idx)
                val childName = "[$idx]"
                diffNode(
                    name = childName,
                    segments = childSegments,
                    left = if (right == null) value else null,
                    right = if (right == null) null else value,
                    options = options
                )
            }
        }
        else -> emptyList()
    }
}

private fun comparePrimitives(
    left: JsonPrimitive,
    right: JsonPrimitive,
    options: DiffOptions
): DiffKind {
    val leftKind = primitiveKind(left)
    val rightKind = primitiveKind(right)
    if (leftKind != rightKind) return DiffKind.TypeChanged

    return when (leftKind) {
        PrimitiveKind.Number -> {
            val leftNum = left.doubleOrNull
            val rightNum = right.doubleOrNull
            if (leftNum == null || rightNum == null) {
                if (left.content == right.content) DiffKind.Unchanged else DiffKind.Changed
            } else {
                val tolerance = options.numberTolerance.coerceAtLeast(0.0)
                if (abs(leftNum - rightNum) <= tolerance) {
                    DiffKind.Unchanged
                } else {
                    DiffKind.Changed
                }
            }
        }
        PrimitiveKind.String,
        PrimitiveKind.Boolean,
        PrimitiveKind.Null -> {
            if (left.content == right.content) DiffKind.Unchanged else DiffKind.Changed
        }
    }
}

private enum class PrimitiveKind {
    String,
    Number,
    Boolean,
    Null
}

private fun primitiveKind(value: JsonPrimitive): PrimitiveKind {
    if (value.isString) return PrimitiveKind.String
    if (value.booleanOrNull != null) return PrimitiveKind.Boolean
    if (value.doubleOrNull != null) return PrimitiveKind.Number
    if (value.content == "null") return PrimitiveKind.Null
    return PrimitiveKind.String
}

private fun collectEntries(node: DiffNode, entries: MutableList<DiffEntry>) {
    if (node.kind != DiffKind.Unchanged) {
        val isLeaf = node.children.isEmpty() ||
            node.kind == DiffKind.Added ||
            node.kind == DiffKind.Removed ||
            node.kind == DiffKind.TypeChanged
        if (isLeaf) {
            entries += DiffEntry(
                kind = node.kind,
                path = node.path,
                displayPath = node.displayPath,
                left = node.left,
                right = node.right
            )
            return
        }
    }

    node.children.forEach { child ->
        collectEntries(child, entries)
    }
}

private fun buildPatch(entries: List<DiffEntry>): List<JsonPatchOp> {
    return entries.mapNotNull { entry ->
        when (entry.kind) {
            DiffKind.Added -> JsonPatchOp(
                op = "add",
                path = entry.path,
                value = entry.right ?: JsonNull
            )
            DiffKind.Removed -> JsonPatchOp(
                op = "remove",
                path = entry.path
            )
            DiffKind.Changed,
            DiffKind.TypeChanged -> JsonPatchOp(
                op = "replace",
                path = entry.path,
                value = entry.right ?: JsonNull
            )
            DiffKind.Unchanged -> null
        }
    }
}

private fun filterTree(root: DiffNode, options: DiffOptions): DiffNode {
    if (options.includePaths.isEmpty()) return root
    val filtered = filterTreeOrNull(root, options)
    return filtered ?: root.copy(kind = DiffKind.Unchanged, children = emptyList())
}

private fun filterTreeOrNull(node: DiffNode, options: DiffOptions): DiffNode? {
    val filteredChildren = node.children.mapNotNull { filterTreeOrNull(it, options) }
    val match = matchesAnyPath(options.includePaths, node.path)
    val hasDiff = node.kind != DiffKind.Unchanged

    return when {
        filteredChildren.isNotEmpty() -> node.copy(children = filteredChildren)
        match && hasDiff -> node.copy(children = emptyList())
        else -> null
    }
}

private fun matchesAnyPath(patterns: List<String>, path: String): Boolean {
    if (patterns.isEmpty()) return true
    val matchPath = if (path.isBlank()) "/" else path
    return patterns.any { pattern ->
        pathMatches(pattern, matchPath)
    }
}

private fun pathMatches(pattern: String, path: String): Boolean {
    val normalized = normalizePattern(pattern)
    val regex = patternToRegex(normalized)
    return regex.matches(path)
}

private fun normalizePattern(pattern: String): String {
    val trimmed = pattern.trim()
    if (trimmed.isEmpty()) return "/*"
    return if (trimmed.startsWith("/")) trimmed else "/$trimmed"
}

private fun patternToRegex(pattern: String): Regex {
    val sb = StringBuilder()
    var i = 0
    while (i < pattern.length) {
        val c = pattern[i]
        if (c == '*') {
            if (i + 1 < pattern.length && pattern[i + 1] == '*') {
                sb.append(".*")
                i += 2
            } else {
                sb.append("[^/]+")
                i += 1
            }
        } else {
            sb.append(Regex.escape(c.toString()))
            i += 1
        }
    }
    return Regex("^$sb$")
}

private fun pointerPath(segments: List<PathSegment>): String {
    if (segments.isEmpty()) return ""
    return segments.joinToString(separator = "/", prefix = "/") { segment ->
        when (segment) {
            is PathSegment.Key -> escapePointer(segment.value)
            is PathSegment.Index -> segment.value.toString()
        }
    }
}

private fun displayPath(segments: List<PathSegment>): String {
    if (segments.isEmpty()) return "$"
    val sb = StringBuilder("$")
    for (segment in segments) {
        when (segment) {
            is PathSegment.Index -> sb.append("[").append(segment.value).append("]")
            is PathSegment.Key -> {
                if (segment.value.matches(Regex("[A-Za-z0-9_]+"))) {
                    sb.append(".").append(segment.value)
                } else {
                    sb.append("['").append(segment.value.replace("'", "\\'")).append("']")
                }
            }
        }
    }
    return sb.toString()
}

private fun escapePointer(segment: String): String {
    return segment.replace("~", "~0").replace("/", "~1")
}

private fun canonicalize(element: JsonElement, options: DiffOptions): String {
    return when (element) {
        is JsonObject -> {
            val keys = element.keys.sorted()
            val body = keys.joinToString(separator = ",") { key ->
                val value = element[key] ?: JsonNull
                "\"${escapeJson(key)}\":${canonicalize(value, options)}"
            }
            "{$body}"
        }
        is JsonArray -> {
            val list = element.toList()
            val arranged = if (options.ignoreArrayOrder) {
                list.sortedBy { canonicalize(it, options) }
            } else {
                list
            }
            val body = arranged.joinToString(separator = ",") { canonicalize(it, options) }
            "[$body]"
        }
        is JsonPrimitive -> {
            if (element.isString) {
                "\"${escapeJson(element.content)}\""
            } else {
                val number = element.doubleOrNull
                val tolerance = options.numberTolerance
                if (number != null && tolerance > 0.0) {
                    val rounded = round(number / tolerance) * tolerance
                    rounded.toString()
                } else {
                    element.toString()
                }
            }
        }
        else -> element.toString()
    }
}

private fun escapeJson(value: String): String {
    return buildString {
        value.forEach { ch ->
            when (ch) {
                '\\' -> append("\\\\")
                '"' -> append("\\\"")
                '\b' -> append("\\b")
                '\u000C' -> append("\\f")
                '\n' -> append("\\n")
                '\r' -> append("\\r")
                '\t' -> append("\\t")
                else -> append(ch)
            }
        }
    }
}
